// Animation styles as a pure function of time, shared by the Visor, export
// and editor. Uses Framer Motion's easings and springs.
import {
  anticipate,
  backIn,
  backInOut,
  backOut,
  circIn,
  circInOut,
  circOut,
  cubicBezier,
  easeIn,
  easeInOut,
  easeOut,
  interpolate,
  spring,
} from "framer-motion";
import { backOutStrong, bounceOut, CUSTOM_EASE_FADE_SHARE } from "./easings";

const EASINGS = {
  linear: (p) => p,
  easeIn,
  easeOut,
  easeInOut,
  circIn,
  circOut,
  circInOut,
  backIn,
  backOut,
  backInOut,
  anticipate,
  bounceOut,
  backOutStrong,
};
// Opacity must not follow a bounce or overshoot (it flickers): it ramps linearly.
const LINEAR_FADE_EASES = new Set(["bounceOut", "backOutStrong"]);

const num = (v, d) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : d;
};

function resolveEase(ease) {
  if (typeof ease === "function") return ease;
  if (Array.isArray(ease) && ease.length === 4) return cubicBezier(...ease);
  return EASINGS[ease] || easeOut;
}

const springCache = new Map();
function springAt(durationSec, bounce, tSec) {
  const key = `${durationSec}|${bounce}`;
  let gen = springCache.get(key);
  if (!gen) {
    gen = spring({ keyframes: [0, 1], duration: durationSec * 1000, bounce: num(bounce, 0.25) });
    springCache.set(key, gen);
  }
  return gen.next(tSec * 1000).value;
}

// Eased progress `tSec` after the animation start. Springs can pass 1.
export function easedProgress(transition = {}, tSec) {
  const duration = Math.max(0.001, num(transition.duration, 1));
  const t = tSec - num(transition.delay, 0);
  if (!(t > 0)) return 0;
  if (transition.type === "spring") return springAt(duration, transition.bounce, t);
  return resolveEase(transition.ease)(Math.min(1, t / duration));
}

// Opacity progress: the eased progress, or a linear ramp over the first part
// of the duration for bounce/overshoot easings.
export function opacityProgress(transition = {}, tSec) {
  if (!LINEAR_FADE_EASES.has(transition.ease)) return easedProgress(transition, tSec);
  const duration = Math.max(0.001, num(transition.duration, 1));
  const t = tSec - num(transition.delay, 0);
  if (!(t > 0)) return 0;
  return Math.min(1, t / (duration * CUSTOM_EASE_FADE_SHARE));
}

// Preset offsets are px on a 1100 px stage (font base 14px). Scale them with
// the stage so the motion stays proportional.
export function stagePxScale(el) {
  const canvas = el && el.closest && el.closest(".canvasSliClass");
  const px = canvas ? parseFloat(canvas.style.fontSize) : 0;
  return px > 0 ? px / 14 : 1;
}

// Same transform order as Framer Motion.
const TRANSFORMS = [
  ["x", (v, k) => `translateX(${v * k}px)`, 0],
  ["y", (v, k) => `translateY(${v * k}px)`, 0],
  ["scale", (v) => `scale(${v})`, 1],
  ["scaleX", (v) => `scaleX(${v})`, 1],
  ["scaleY", (v) => `scaleY(${v})`, 1],
  ["rotate", (v) => `rotate(${v}deg)`, 0],
  ["rotateX", (v) => `rotateX(${v}deg)`, 0],
  ["rotateY", (v) => `rotateY(${v}deg)`, 0],
];

const mixValue = (from, to, p, fallback) => {
  if (from === undefined) return to === undefined ? fallback : to;
  if (to === undefined) return from;
  if (typeof from === "number" && typeof to === "number") return from + (to - from) * p;
  if (typeof from === "string" && typeof to === "string") {
    try {
      return interpolate([0, 1], [from, to], { clamp: false })(p);
    } catch (_) {
      return p < 0.5 ? from : to;
    }
  }
  return p < 0.5 ? from : to;
};

// Styles between `initial` and `animate` at eased progress `p`.
export function sampleStyle(initial = {}, animate = {}, p, pxScale = 1, pOpacity = p) {
  const out = {};
  const transforms = [];
  TRANSFORMS.forEach(([key, fmt, dflt]) => {
    if (initial[key] === undefined && animate[key] === undefined) return;
    transforms.push(fmt(mixValue(initial[key], animate[key], p, dflt), pxScale));
  });
  if (transforms.length) out.transform = transforms.join(" ");
  ["opacity", "clipPath", "width"].forEach((key) => {
    if (initial[key] === undefined && animate[key] === undefined) return;
    out[key] = mixValue(initial[key], animate[key], key === "opacity" ? pOpacity : p);
  });
  return out;
}

export function styleAt({ initial, animate, transition }, tSec, pxScale = 1) {
  return sampleStyle(
    initial,
    animate,
    easedProgress(transition, tSec),
    pxScale,
    opacityProgress(transition, tSec)
  );
}

// `baseTransform` (the box rotation) is applied before the animation.
export function applyStyle(el, style, baseTransform = "") {
  if (!el) return;
  if (style.transform !== undefined) {
    el.style.transform = baseTransform ? `${baseTransform} ${style.transform}` : style.transform;
  }
  if (style.opacity !== undefined) el.style.opacity = String(style.opacity);
  if (style.clipPath !== undefined) el.style.clipPath = String(style.clipPath);
  if (style.width !== undefined) {
    el.style.width = typeof style.width === "number" ? `${style.width}px` : String(style.width);
  }
}
